import React, { useState } from 'react';
import { ILocalAddress, IOrderDetailResponse, IShipTo, ISoldTo } from '../../../actions/order-details/get-order-details.action';
import { NO_DATA_FALLBACK } from '../../../configuration';
import { formatAddress } from '../../../utilities/format-address';
import { IOrderDetailsResources } from '../order-details.props.autogenerated';

interface IOrderDetailsAddressCardProps {
    CLASS_NAME: string;
    resources: IOrderDetailsResources;
    addressInformation?: ISoldTo | IShipTo;
    headline: string;
    hasAnyLocal: boolean;
    showGlobalAddress: boolean;
    onToggle: () => void;
}

const isValidLocalAddress = (localAddr?: ILocalAddress): boolean => {
    if (!localAddr) {
        return false;
    }

    for (let i = 1; i <= 9; i++) {
        const val = localAddr[`ShortAddress${i}` as keyof ILocalAddress];
        if (typeof val === 'string' && val.trim() !== '') {
            return true;
        }
    }
    return false;
};

const LocalSwitchToggle: React.FC<{ label: string; CLASS_NAME: string; onClick: () => void }> = ({ label, CLASS_NAME, onClick }) => (
    <a className={`${CLASS_NAME}--local-switch`} onClick={onClick}>
        {label}
    </a>
);

const AddressCard: React.FC<IOrderDetailsAddressCardProps> = ({
    CLASS_NAME,
    resources,
    addressInformation,
    headline,
    hasAnyLocal,
    showGlobalAddress,
    onToggle
}) => {
    const addressToRender = hasAnyLocal && !showGlobalAddress ? addressInformation?.LocalAddress : addressInformation;
    const hasData = addressToRender && Object.keys(addressToRender).length > 0;
    const addressLines = hasData ? formatAddress(addressToRender) : [];

    return (
        <div className={`${CLASS_NAME}__address-card-content`}>
            <h5 className={`${CLASS_NAME}__address-card-title`}>
                {`${headline}${addressInformation?.Id ? `: ${addressInformation.Id}` : ''}`}
            </h5>
            <div className={`${CLASS_NAME}__address-card-body`}>
                <div className={`${CLASS_NAME}__address-card-lines`}>
                    {hasData ? addressLines.map((line, index) => <p key={index}>{line}</p>) : <p>{NO_DATA_FALLBACK}</p>}
                </div>

                {hasAnyLocal && (
                    <div className={`${CLASS_NAME}__address-card--local-switch`}>
                        <LocalSwitchToggle
                            label={
                                showGlobalAddress
                                    ? resources.orderDetailsAddressInformationLocalSwitchButton
                                    : resources.orderDetailsAddressInformationGlobalSwitchButton
                            }
                            CLASS_NAME={CLASS_NAME}
                            onClick={onToggle}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const AddressInformation = ({ orderDetails, resources }: { orderDetails: IOrderDetailResponse; resources: IOrderDetailsResources }) => {
    const CLASS_NAME = 'ms-order-details';

    const { SoldTo, ShipTo } = orderDetails.OrderDetail;

    const hasSoldToLocal = isValidLocalAddress(SoldTo?.LocalAddress);
    const hasShipToLocal = isValidLocalAddress(ShipTo?.LocalAddress);

    const [showGlobalAddress, setShowGlobalAddress] = useState<boolean>(() => {
        const stored = localStorage.getItem(AddressInformation.name);
        return stored !== null ? JSON.parse(stored) : true;
    });

    const __handleAddressLocalChange = () => {
        setShowGlobalAddress(prev => {
            const newValue = !prev;
            localStorage.setItem(AddressInformation.name, JSON.stringify(newValue));
            return newValue;
        });
    };

    return (
        <div className={`${CLASS_NAME}__address-card-container`}>
            <div className={`${CLASS_NAME}__address-card-wrapper`}>
                <div className={`${CLASS_NAME}__address-card`}>
                    <AddressCard
                        CLASS_NAME={CLASS_NAME}
                        resources={resources}
                        addressInformation={SoldTo}
                        headline={resources.orderDetailsSoldTo}
                        hasAnyLocal={hasSoldToLocal}
                        showGlobalAddress={showGlobalAddress}
                        onToggle={__handleAddressLocalChange}
                    />
                </div>
                <div className={`${CLASS_NAME}__address-card`}>
                    <AddressCard
                        CLASS_NAME={CLASS_NAME}
                        resources={resources}
                        addressInformation={ShipTo}
                        headline={resources.orderDetailsShipTo}
                        hasAnyLocal={hasShipToLocal}
                        showGlobalAddress={showGlobalAddress}
                        onToggle={__handleAddressLocalChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default AddressInformation;
